import {
  LineChart,
  formatPercentage,
  CurrencyValue,
  Flex,
  ChartSkeleton,
  formatCurrency,
  EmptyBox
} from '@blockanalitica/ui';
import { getCollateralEmptyBoxText } from '@/components';
import type { SimpleChartData } from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import { ChartConfig } from '@blockanalitica/ui/package/types/src/components/charts/Chart';
import { useMemo } from 'react';
import { useApi } from '@/api';

type AtRiskData = {
  results: SimpleChartData[];
};

export default function AtRiskChart({
  marketAddress,
  tag,
  symbol,
  ...rest
}: {
  marketAddress: string;
  tag: string;
  symbol: string;
}) {
  const defaultConfig: ChartConfig = useMemo(() => {
    return {
      options: {
        scales: {
          x: {
            type: 'category'
          }
        }
      }
    };
  }, []);

  const yScaleValueFormatter = (value: string | number) => {
    return formatCurrency(Number(value), { maximumFractionDigits: 5 });
  };
  const valueFormatter = (value: string | number) => {
    return (
      <CurrencyValue
        value={value}
        variant="dynamic"
        options={{ currency: 'USD' }}
      />
    );
  };

  const xScaleValueFormatter = (value: string | number) => {
    return formatPercentage(Number(value) / 100);
  };

  const subValueFormatter = (value: string | number) => {
    return formatPercentage(Number(value));
  };

  const [data, error] = useApi<AtRiskData>({
    url: `/markets/${marketAddress}/at-risk/`
  });
  const datasets = [
    {
      xField: 'price_change',
      yField: 'collateral_liquidated_cumulative',
      label: 'At Risk'
    }
  ];

  if (error) {
    throw error;
  }

  if (!data) {
    return (
      <Flex flexDirection="column" gap="xs" {...rest}>
        <ChartSkeleton />
      </Flex>
    );
  }
  return (
    <LineChart
      data={data.results}
      datasets={datasets}
      valueFormatter={valueFormatter}
      yScaleValueFormatter={yScaleValueFormatter}
      xScaleValueFormatter={xScaleValueFormatter}
      subValueFormatter={subValueFormatter}
      config={defaultConfig}
      emptyComponent={
        <EmptyBox message={getCollateralEmptyBoxText(tag, symbol)} />
      }
    />
  );
}
